import React from 'react';

import { EmojiPickerComboFloating } from '@pkg/components/GlobalSearch/EmojiPickerComboFloating';
import { EmojiPickerWrapper } from '@pkg/components/GlobalSearch/SearchInput/styles';
import { Badge } from '@pkg/components/Sections';
import { FilterAction, FilterDropdown, type FilterDropdownProps, FiltersBar } from '@pkg/components/filters';
import { FieldDirection, type FieldDirectionProps, FieldSearch, type FieldSearchProps } from '@pkg/components/forms';
import { Direction, Visibility, YatField } from '@pkg/graphql/codegen/graphql';

import { useYatFiltersStore } from '@app/store/zustand';

const SORT_FIELD_OPTIONS: FilterDropdownProps['options'] = [
    { id: YatField.OwnershipDate, label: 'Date added' },
    { id: YatField.RhythmScore, label: 'Rhythm score' },
];

function assertYatField(value = ''): value is YatField {
    // Not sure how else to avoid this for passing to <FilterDropdown />.
    return Boolean(value === YatField.OwnershipDate || value === YatField.RhythmScore);
}

export function YatFilters() {
    const search = useYatFiltersStore((state) => state.search);
    const sortField = useYatFiltersStore((state) => state.sortField);
    const sortDirection = useYatFiltersStore((state) => state.sortDirection);
    const resultsCount = useYatFiltersStore((state) => state.resultsCount);
    const visibility = useYatFiltersStore((state) => state.visibility);
    const setSearch = useYatFiltersStore((state) => state.setSearch);
    const setSortField = useYatFiltersStore((state) => state.setSortField);
    const setSortDirection = useYatFiltersStore((state) => state.setSortDirection);
    const setVisibility = useYatFiltersStore((state) => state.setVisibility);

    function handleSelectEmoji(selectedEmoji = '') {
        setSearch(search + selectedEmoji);
    }

    const handleSearch: FieldSearchProps['onChange'] = (newValue = '') => {
        const stringified = newValue.toString();
        setSearch(stringified);
    };

    function handleSortFilterSelect(id = '') {
        if (assertYatField(id)) setSortField(id);
    }

    const handleDirectionToggle: FieldDirectionProps['onToggle'] = (newDirection) => {
        setSortDirection(newDirection === 'desc' ? Direction.Desc : Direction.Asc);
    };

    const isHidden = visibility === Visibility.Hidden;
    const isVisible = visibility === Visibility.Visible;

    const emojiPickerMarkup = (
        <EmojiPickerWrapper>
            <EmojiPickerComboFloating
                onSelectEmoji={handleSelectEmoji}
                autoPlacementOptions={{ allowedPlacements: ['right-start', 'top-end', 'bottom-end'] }}
            />
        </EmojiPickerWrapper>
    );

    return (
        <FiltersBar>
            <FiltersBar.Layout>
                <FiltersBar.Group>
                    <FilterAction
                        label="Hidden Yats"
                        pressed={isHidden}
                        onAction={() => setVisibility(isHidden ? undefined : Visibility.Hidden)}
                    >
                        {resultsCount?.hidden === undefined ? null : (
                            <Badge variant="round">{resultsCount.hidden}</Badge>
                        )}
                    </FilterAction>

                    <FilterAction
                        label="Visible to everyone"
                        pressed={isVisible}
                        onAction={() => setVisibility(isVisible ? undefined : Visibility.Visible)}
                    >
                        {resultsCount?.visible === undefined ? null : (
                            <Badge variant="round">{resultsCount.visible}</Badge>
                        )}
                    </FilterAction>
                </FiltersBar.Group>

                <FiltersBar.SearchWrapper>
                    <FieldSearch
                        value={search}
                        placeholder="Search for your Yat"
                        suffix={emojiPickerMarkup}
                        onChange={handleSearch}
                    />
                </FiltersBar.SearchWrapper>

                <FiltersBar.Group>
                    <FilterDropdown
                        options={SORT_FIELD_OPTIONS}
                        selectedId={sortField}
                        onSelectOption={handleSortFilterSelect}
                    />

                    <FieldDirection
                        direction={sortDirection === Direction.Desc ? 'desc' : 'asc'}
                        onToggle={handleDirectionToggle}
                    />
                </FiltersBar.Group>
            </FiltersBar.Layout>
        </FiltersBar>
    );
}
